import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BriefcaseBusiness, CircleUserRound, FilePlus2, FolderKanban, Inbox } from "lucide-react";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const navItems = [
  { href: "/dashboard/employer", label: "Overview", icon: BriefcaseBusiness },
  { href: "/dashboard/stb/create", label: "Neue Anzeige", icon: FilePlus2 },
  { href: "/dashboard/stb/manage", label: "Verwalten", icon: FolderKanban },
  { href: "/dashboard/stb/applications", label: "Bewerbungen", icon: Inbox },
  { href: "/dashboard/account", label: "Account", icon: CircleUserRound },
];

const DECISION_STATE_OPTIONS = [
  { value: "new", label: "Neu" },
  { value: "review", label: "In Prüfung" },
  { value: "shortlist", label: "Interessant" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "hired", label: "Eingestellt" },
];

const VERIFICATION_STATUS_LABELS = {
  declared: "Declared",
  uploaded: "Uploaded",
  pending_review: "Pending Review",
  formally_checked: "Checked",
  online_verifiable: "Online Verifiable",
  online_verified: "Online Verified",
  inconsistent: "Inconsistent",
  suspicious: "Suspicious",
  rejected: "Rejected",
};

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDecisionState(value) {
  return DECISION_STATE_OPTIONS.find((option) => option.value === value)?.label || value || "Neu";
}

function formatBoolean(value) {
  return value ? "Ja" : "Nein";
}

function buildInitialDecisionForm(detail) {
  return {
    decision_state: detail?.decision_preparation?.decision_state || detail?.application_status || "new",
    decision_note: detail?.decision_preparation?.decision_note || "",
  };
}

function createEmptyDetailState() {
  return {
    data: null,
    form: { decision_state: "new", decision_note: "" },
    error: "",
    flash: "",
    loading: false,
    saving: false,
  };
}

function formatVerificationSummary(byStatus) {
  const entries = Object.entries(byStatus || {}).filter(([, count]) => Number(count || 0) > 0);
  if (!entries.length) return "Nur Kompaktstatus, keine Rohsicht auf Referenzen.";
  return entries
    .map(([status, count]) => `${VERIFICATION_STATUS_LABELS[status] || status}: ${count}`)
    .join(" · ");
}

const StbApplications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [adId, setAdId] = useState(searchParams.get("adId") || "");
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [detailsById, setDetailsById] = useState({});

  const updateDetailState = (applicationId, updater) => {
    const key = String(applicationId);
    setDetailsById((current) => {
      const previous = current[key] || createEmptyDetailState();
      const next = typeof updater === "function" ? updater(previous) : { ...previous, ...updater };
      return { ...current, [key]: next };
    });
  };

  const loadAds = async () => {
    const res = await axios.get(`${config.API_BASE_URL}/api/stb/employer/ads`, {
      params: { page: 1, limit: 100 },
      headers: authHeaders(),
    });
    const list = res.data?.rows || [];
    setAds(list);
    if (!adId && list[0]?.id) setAdId(String(list[0].id));
  };

  const loadApplications = async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/stb/employer/ads/${id}/applications`, {
        headers: authHeaders(),
      });
      setRows(res.data?.rows || []);
      setExpandedId("");
      setDetailsById({});
      setMessage("");
    } catch (e) {
      setMessage(e.response?.data?.message || "Bewerbungen konnten nicht geladen werden.");
      setRows([]);
      setExpandedId("");
      setDetailsById({});
    }
  };

  const loadDecisionSummary = async (applicationId) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      loading: true,
      error: "",
      flash: "",
    }));
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/decision-handoff`,
        { headers: authHeaders() }
      );
      updateDetailState(applicationId, {
        data: res.data,
        form: buildInitialDecisionForm(res.data),
        loading: false,
        saving: false,
        error: "",
        flash: "",
      });
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        loading: false,
        error: e.response?.data?.message || "Decision Summary konnte nicht geladen werden.",
      }));
    }
  };

  const toggleDecisionSummary = async (applicationId) => {
    const key = String(applicationId);
    if (expandedId === key) {
      setExpandedId("");
      return;
    }

    setExpandedId(key);
    const current = detailsById[key];
    if (!current?.data && !current?.loading) {
      await loadDecisionSummary(applicationId);
    }
  };

  const handleDecisionFormChange = (applicationId, field, value) => {
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      form: {
        ...previous.form,
        [field]: value,
      },
      flash: "",
      error: "",
    }));
  };

  const saveDecisionPreparation = async (applicationId) => {
    const key = String(applicationId);
    const current = detailsById[key] || createEmptyDetailState();
    updateDetailState(applicationId, (previous) => ({
      ...previous,
      saving: true,
      error: "",
      flash: "",
    }));

    try {
      const res = await axios.patch(
        `${config.API_BASE_URL}/api/stb/employer/applications/${applicationId}/decision-preparation`,
        current.form,
        { headers: authHeaders() }
      );

      const updated = res.data;
      setRows((currentRows) => currentRows.map((row) => (
        row.id === applicationId
          ? { ...row, status: updated.application_status }
          : row
      )));
      updateDetailState(applicationId, {
        data: updated,
        form: buildInitialDecisionForm(updated),
        loading: false,
        saving: false,
        error: "",
        flash: "Interne Entscheidungsvorbereitung gespeichert.",
      });
    } catch (e) {
      updateDetailState(applicationId, (previous) => ({
        ...previous,
        saving: false,
        error: e.response?.data?.message || "Decision Preparation konnte nicht gespeichert werden.",
      }));
    }
  };

  useEffect(() => {
    document.title = "Experts4Europe | Employer Bewerbungen";
    loadAds().catch(() => setMessage("Anzeigen konnten nicht geladen werden."));
  }, []);

  useEffect(() => {
    if (!adId) return;
    loadApplications(adId);
    setSearchParams({ adId });
  }, [adId, setSearchParams]);

  return (
    <CockpitShell
      title="Bewerbungen mit Decision Summary"
      subtitle="Die Bewerbungsansicht bleibt Teil desselben Hiring Control Decks und fuehrt direkt in die kompakte Decision Summary."
      badge="Employer Applications"
      navItems={navItems}
      wideLayout
      theme="employer"
      shellLabel="Applications"
      shellDescription="Folgeflaeche im selben Employer-Template fuer Bewerbungen, Decision Summary und interne Entscheidungsvorbereitung."
      modeLabel="Decision Flow"
      liveLabel="Employer"
      topSignal="Employer System"
      topContext="Bewerbungen"
      actions={(
        <Button
          variant="outline"
          className="dashboard-action-button dashboard-action-button-secondary"
          onClick={() => loadApplications(adId)}
        >
          Refresh
        </Button>
      )}
    >
      {message ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {message}
        </p>
      ) : null}

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Anzeige auswaehlen</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={adId} onValueChange={setAdId}>
            <SelectTrigger className="dashboard-field max-w-xl">
              <SelectValue placeholder="Anzeige waehlen" />
            </SelectTrigger>
            <SelectContent>
              {ads.map((ad) => (
                <SelectItem key={ad.id} value={String(ad.id)}>
                  #{ad.id} · {ad.titel} · {ad.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Anonyme Bewerbungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-600">
          {rows.length === 0 ? <p className="text-slate-500">Keine Bewerbungen vorhanden.</p> : null}
          {rows.map((row) => {
            const detailState = detailsById[String(row.id)] || createEmptyDetailState();
            const detail = detailState.data;
            const candidateCore = detail?.decision_handoff?.candidate_core || {};
            const verification = detail?.decision_handoff?.verification?.references || {};
            const languageSignal = detail?.decision_handoff?.language_test_b1_core || { shared: false };
            const wtSummary = detail?.decision_handoff?.wt_summary || { shared: false };

            return (
              <div key={row.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-slate-900">
                      Bewerbung #{row.id} · {row.candidate_ref || "CAND-pending"}
                    </p>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Aktueller Status: {formatDecisionState(row.status)}
                    </p>
                    {row.wt_shared && row.wt_summary ? (
                      <p className="text-sky-700">
                        Wissenstest geteilt: {row.wt_summary.score_percent}% ({row.wt_summary.correct_count}/{row.wt_summary.total_count})
                      </p>
                    ) : (
                      <p className="text-slate-400">Kein geteilter Wissenstest.</p>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className="dashboard-inline-button"
                    onClick={() => toggleDecisionSummary(row.id)}
                  >
                    {expandedId === String(row.id) ? "Summary ausblenden" : "Decision Summary"}
                  </Button>
                </div>

                {expandedId === String(row.id) ? (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                    {detailState.loading ? <p className="text-slate-500">Decision Summary wird geladen...</p> : null}
                    {detailState.error ? (
                      <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                        {detailState.error}
                      </p>
                    ) : null}

                    {detail ? (
                      <div className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Application</p>
                            <p className="mt-2 text-slate-900">#{detail.application_id}</p>
                            <p className="mt-1 text-slate-600">{detail.candidate_ref || "CAND-pending"}</p>
                            <p className="mt-1 text-sky-700">{formatDecisionState(detail.application_status)}</p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Candidate Core</p>
                            <p className="mt-2 text-slate-700">Profilbasis: {formatBoolean(candidateCore.profile_basis_ready)}</p>
                            <p className="text-slate-700">CV-Kern: {formatBoolean(candidateCore.cv_core_ready)}</p>
                            <p className="mt-2 text-slate-600">
                              Skills {candidateCore.skills?.count || 0} · Sprachen {candidateCore.languages?.count || 0}
                            </p>
                            <p className="text-slate-600">
                              Ausbildung {candidateCore.education?.count || 0} · Erfahrung {candidateCore.experience?.count || 0}
                            </p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Verification</p>
                            <p className="mt-2 text-slate-700">Nachweise vorhanden: {formatBoolean(verification.present)}</p>
                            <p className="text-slate-700">Referenzen gesamt: {verification.count || 0}</p>
                            <p className="mt-2 text-slate-600">{formatVerificationSummary(verification.by_status)}</p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">WT Summary</p>
                            {wtSummary.shared && wtSummary.summary ? (
                              <>
                                <p className="mt-2 text-slate-700">Geteilt: Ja</p>
                                <p className="text-sky-700">
                                  {wtSummary.summary.score_percent}% ({wtSummary.summary.correct_count}/{wtSummary.summary.total_count})
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="mt-2 text-slate-700">Geteilt: Nein</p>
                                <p className="text-slate-600">WT bleibt optionales Zusatzsignal.</p>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4">
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Language Test B1 Core</p>
                          {languageSignal.shared && languageSignal.signal ? (
                            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Band</p>
                                <p className="mt-2 text-xl font-semibold text-slate-900">{languageSignal.signal.estimated_cefr_band || "n/a"}</p>
                                <p className="mt-1 text-slate-600">Overall {languageSignal.signal.overall_language_score || "n/a"}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Test</p>
                                <p className="mt-2 text-slate-900">{languageSignal.signal.test_type || "n/a"}</p>
                                <p className="mt-1 text-slate-600">Review {languageSignal.signal.review_status || "n/a"}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Progress</p>
                                <p className="mt-2 text-slate-900">{languageSignal.signal.progress?.trend || "baseline"}</p>
                                <p className="mt-1 text-slate-600">Delta {languageSignal.signal.progress?.delta ?? "n/a"}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Module</p>
                                <p className="mt-2 text-slate-600">
                                  Lesen {languageSignal.signal.module_scores?.reading ?? "n/a"} · Hoeren {languageSignal.signal.module_scores?.listening ?? "n/a"}
                                </p>
                                <p className="text-slate-600">
                                  Bausteine {languageSignal.signal.module_scores?.language_elements ?? "n/a"} · Schreiben {languageSignal.signal.module_scores?.writing ?? "n/a"} · Sprechen {languageSignal.signal.module_scores?.speaking ?? "n/a"}
                                </p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 md:col-span-2 xl:col-span-4">
                                <p className="text-slate-700">{languageSignal.signal.summary?.summary || "Keine kompakte Sprachsummary vorhanden."}</p>
                                {languageSignal.signal.summary?.caution ? (
                                  <p className="mt-2 text-amber-700">{languageSignal.signal.summary.caution}</p>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <p className="mt-3 text-slate-600">
                              Kein freigegebenes B1-Core-Sprachsignal vorhanden. Keine Rohtexte, keine Transkripte und keine Detailrubrics im Employer-Pfad.
                            </p>
                          )}
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Interne Entscheidungsvorbereitung</p>
                              <p className="mt-2 text-slate-600">
                                Nutzt den bestehenden Bewerbungsstatus plus kurze interne Notiz. Keine Team-Workflows, kein Ranking.
                              </p>
                            </div>
                            <p className="text-xs text-slate-400">
                              Zuletzt aktualisiert: {detail.decision_preparation?.updated_at || "n/a"}
                            </p>
                          </div>

                          <div className="mt-4 grid gap-4 lg:grid-cols-[220px,1fr]">
                            <div>
                              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-400">
                                Decision State
                              </label>
                              <Select
                                value={detailState.form.decision_state}
                                onValueChange={(value) => handleDecisionFormChange(row.id, "decision_state", value)}
                              >
                                <SelectTrigger className="dashboard-field">
                                  <SelectValue placeholder="Status waehlen" />
                                </SelectTrigger>
                                <SelectContent>
                                  {DECISION_STATE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-slate-400">
                                Interne Notiz
                              </label>
                              <textarea
                                value={detailState.form.decision_note}
                                onChange={(event) => handleDecisionFormChange(row.id, "decision_note", event.target.value)}
                                maxLength={500}
                                rows={4}
                                placeholder="Kurze interne Einordnung zur Entscheidungsvorbereitung."
                                className="dashboard-field dashboard-textarea w-full"
                              />
                              <p className="mt-1 text-right text-xs text-slate-500">
                                {detailState.form.decision_note.length}/500
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <p className="text-xs text-slate-400">
                              Sichtgrenze: keine Rohdokumente, keine Dokumentpfade, keine Review-Metadaten, keine Suspicion-Details.
                            </p>
                            <Button
                              className="dashboard-action-button"
                              onClick={() => saveDecisionPreparation(row.id)}
                              disabled={detailState.saving}
                            >
                              {detailState.saving ? "Speichert..." : "Decision Prep speichern"}
                            </Button>
                          </div>

                          {detailState.flash ? <p className="mt-3 text-sm text-emerald-700">{detailState.flash}</p> : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </CockpitShell>
  );
};

export default StbApplications;
